import { truncate } from "@salestrak/utilities";

/**
 * Compact numeric formatter for chart axes/labels. The kit OWNS this logic and
 * EXPOSES it — it does not auto-apply it. Opt in per axis via visx's `tickFormat`
 * (e.g. `<Axis orientation="left" tickFormat={compactNumber} />`).
 *
 * Values with three or fewer digits render unchanged; 1000+ collapse to lowercase
 * `k`/`M`/`B` with a single decimal only when it carries information (trailing
 * `.0` is stripped): 1000 → "1k", 1500 → "1.5k", 12000 → "12k", 1_250_000 → "1.3M".
 */
export function compactNumber(value: number | { valueOf(): number }): string {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  if (Math.abs(n) < 1000) return String(n);
  const units = [
    [1e9, "B"],
    [1e6, "M"],
    [1e3, "k"],
  ] as const;
  for (const [factor, suffix] of units) {
    if (Math.abs(n) >= factor) {
      // One decimal place, then let toString drop a trailing ".0".
      const scaled = Math.round((n / factor) * 10) / 10;
      return `${scaled}${suffix}`;
    }
  }
  return String(n);
}

/**
 * Ellipsis-truncate a categorical tick label. Opt in per axis via `tickFormat`
 * (e.g. `tickFormat={(v) => truncateLabel(String(v))}`) — the default 8-char
 * budget fits a band-scale bottom axis; pass a larger `max` where the axis has
 * room (angled ticks, wide cards).
 *
 * The default is what the kit contributes; the cut itself is `truncate`.
 */
export function truncateLabel(s: string, max = 8): string {
  return truncate(s, max);
}
